/**
 * LevelOptionDialog — "when NB / NS / BB / BS fires, open this option strategy".
 *
 * Left: the strategy (ready-mades, the level's own direction first), the expiry rule,
 * size, and the exits — ₹ and/or underlying-price TP and SL, whichever is hit first,
 * all NET of slippage and fees (no automatic trailing). Right: a live preview "if it fired at the level now":
 * today's chain re-priced at the level (each contract keeps its own IV), built with
 * utils/optionTemplates.ts — the same bytes the server runs when the level fires.
 *
 * LIVE (real orders) is the default for a new attachment — PAPER is one click away, and an
 * account without an active Delta key starts on PAPER with LIVE disabled. The red
 * "Attach — LIVE" button is the confirmation (it sends confirmLive='LIVE', which the server
 * requires on every LIVE arm). At the fire the server re-reads the wallet and refuses to
 * place when it cannot be read or does not cover the margin + 10%.
 */
import { brokerFundsService, BrokerFunds } from '../../services/brokerFunds.service';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, TextField, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import toast from 'react-hot-toast';
import api from '../../services/api';
import {
  levelOptionTriggersService, LevelKey, ExpiryTier, LevelOptionTrigger, LEVEL_SHORT, TIER_LABEL,
} from '../../services/levelOptionTriggers.service';
import {
  templatesForLevel, findTemplate, resolveExpiry, resolveFixedExpiry, tierOfExpiry, buildTemplate, buildCustomLegs,
  chainAtSpot, legLiquidityProblem, legOffset, tplAtm, DEFAULT_MIN_HOURS, MAX_CUSTOM_LEGS,
  TplRow, TplBuiltLeg, OptionTemplate, TplCustomLeg, TplStrikeMode,
} from '../../utils/optionTemplates';
import { computeStats, WsLeg } from '../charts/workspace/wsMath';
import PayoffGraph from '../charts/workspace/PayoffGraph';
import { useWsColors, fmtStrike, expLabel, timeLeft } from '../charts/workspace/wsUi';
import { isMonthlyExpiry } from '../charts/workspace/wsMath';
import { USD_TO_INR } from '../../utils/constants';

interface Props {
  open: boolean;
  onClose: () => void;
  instrumentId: number;
  symbol: string;              // BTCUSD | ETHUSD
  levelKey: LevelKey;
  levelValue: number | null;   // the level as set now (null = not set yet)
  existing: LevelOptionTrigger | null;
  autoExecute: boolean;
  onSaved: () => void;
}

interface ChainResp {
  spot: number; contractValue: number;
  expiries: Array<{ iso: string; settleMs: number; strikes: number[] }>;
  chain: { iso: string; settleMs: number; rows: TplRow[] } | null;
}

const LONG_KEYS: LevelKey[] = ['nbuy', 'bbuy'];
const DEFAULT_PRE_EXPIRY: Record<ExpiryTier, number> = { D0: 60, D1: 60, W0: 360, M0: 360 };
const inr = (v: number) => `₹${Math.round(v).toLocaleString('en-IN')}`;
const numOrNull = (s: string): number | null => { const n = Number(s); return s.trim() !== '' && Number.isFinite(n) && n > 0 ? n : null; };
const str = (v: number | null | undefined) => (v != null && Number(v) > 0 ? String(v) : '');

/** One-line, beginner wording for what the built position bets on. */
function describe(tpl: OptionTemplate, legs: TplBuiltLeg[] | null, base: string): string {
  if (!legs || !legs.length) return tpl.blurb;
  const k = (t: 'CE' | 'PE', a: 'BUY' | 'SELL') => legs.find(l => l.optionType === t && l.action === a)?.strike;
  const f = (x?: number) => (x != null ? fmtStrike(x) : '?');
  switch (tpl.id) {
    case 'bull_put_spread': return `Sell a put spread below ${f(k('PE', 'SELL'))} — profits if ${base} stays above it.`;
    case 'bear_call_spread': return `Sell a call spread above ${f(k('CE', 'SELL'))} — profits if ${base} stays below it.`;
    case 'long_call': return `Buy the ${f(k('CE', 'BUY'))} call — profits if ${base} rises; risk is the premium paid.`;
    case 'long_put': return `Buy the ${f(k('PE', 'BUY'))} put — profits if ${base} falls; risk is the premium paid.`;
    case 'bull_call_spread': return `Buy ${f(k('CE', 'BUY'))} / sell ${f(k('CE', 'SELL'))} calls — profits on a move up toward ${f(k('CE', 'SELL'))}.`;
    case 'bear_put_spread': return `Buy ${f(k('PE', 'BUY'))} / sell ${f(k('PE', 'SELL'))} puts — profits on a move down toward ${f(k('PE', 'SELL'))}.`;
    default: return tpl.blurb;
  }
}

const Glyph: React.FC<{ pts: string; color: string }> = ({ pts, color }) => (
  <svg width="40" height="20" viewBox="0 0 40 20" style={{ flexShrink: 0 }}>
    <line x1="0" y1="10" x2="40" y2="10" stroke={color} strokeOpacity={0.25} strokeWidth={1} />
    <polyline points={pts} fill="none" stroke={color} strokeWidth={1.8} strokeLinejoin="round" />
  </svg>
);

const LevelOptionDialog: React.FC<Props> = ({ open, onClose, instrumentId, symbol, levelKey, levelValue, existing, autoExecute, onSaved }) => {
  const c = useWsColors();
  const narrow = useMediaQuery('(max-width:900px)');
  const base = symbol.toUpperCase().replace(/USD$/, '');
  const long = LONG_KEYS.indexOf(levelKey) >= 0;
  const label = `${LEVEL_SHORT[levelKey]} ${levelValue != null ? fmtStrike(levelValue) : '(not set)'}`;
  const list = useMemo(() => templatesForLevel(levelKey), [levelKey]);

  // ── form state ──
  const [templateId, setTemplateId] = useState<string>(existing?.templateId || list[0].tpl.id);
  const [tier, setTier] = useState<ExpiryTier>(existing?.expiryTier || 'W0');
  const [minHours, setMinHours] = useState<string>(existing?.minHoursToExpiry != null ? String(existing.minHoursToExpiry) : '');
  // A specific expiry date (null = the tier rule picks it at the fire).
  const [expIso, setExpIso] = useState<string | null>(existing?.expiryIso || null);
  // Edited legs (null = the template picks the strikes at the fire) and how strikes are kept.
  const [editLegs, setEditLegs] = useState<TplCustomLeg[] | null>(existing?.customLegs && existing.customLegs.length ? existing.customLegs.map(l => ({ ...l })) : null);
  const [strikeMode, setStrikeMode] = useState<TplStrikeMode>(existing?.strikeMode === 'FIXED' ? 'FIXED' : existing?.strikeMode === 'OFFSET' ? 'OFFSET' : (existing?.expiryIso ? 'FIXED' : 'OFFSET'));
  const [lots, setLots] = useState<number>(existing?.lots || 1);
  const [maxRisk, setMaxRisk] = useState<string>(str(existing?.maxRiskInr));
  const [tp, setTp] = useState<string>(str(existing?.tpAmountInr));
  const [sl, setSl] = useState<string>(str(existing?.slAmountInr));
  const [tgtU, setTgtU] = useState<string>(str(existing?.tgtUnderlying));
  const [slU, setSlU] = useState<string>(str(existing?.slUnderlying));
  const [preExp, setPreExp] = useState<string>(existing?.preExpiryExitMins != null ? String(existing.preExpiryExitMins) : '');
  const [spread, setSpread] = useState<string>(existing ? String(existing.maxSpreadPct) : '15');
  // New attachments default to LIVE (real orders); an existing one keeps its own mode.
  const [mode, setMode] = useState<'PAPER' | 'LIVE'>(existing ? (existing.tradingMode === 'LIVE' ? 'LIVE' : 'PAPER') : 'LIVE');
  const [funds, setFunds] = useState<BrokerFunds | null>(null);
  const noDeltaKey = funds?.status.delta === 'no_credentials';
  useEffect(() => {
    if (!open) return;
    let dead = false;
    brokerFundsService.getFunds().then(f => {
      if (dead) return;
      setFunds(f);
      // No Delta key → LIVE cannot arm (the server refuses); start a new one on PAPER.
      if (!existing && f?.status?.delta === 'no_credentials') setMode('PAPER');
    }).catch(() => {});
    return () => { dead = true; };
  }, [open, existing]);
  const isLive = mode === 'LIVE';
  const [chase, setChase] = useState<string>(existing ? String(existing.maxChasePct) : '0.5');
  const [adv, setAdv] = useState(false);
  const [touchedExits, setTouchedExits] = useState<boolean>(!!existing);
  const [saving, setSaving] = useState(false);

  // ── live chain ──
  const [chain, setChain] = useState<ChainResp | null>(null);
  const [chainErr, setChainErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());
  const fetchChain = useCallback(async (expiry?: string) => {
    setLoading(true);
    try {
      const r = await api.get<any>('/options/workspace/chain', { params: { base, ...(expiry ? { expiry } : {}) }, headers: { _suppressToast: 'true' } as any });
      setChain(r.data?.data || null); setChainErr(null); setNowMs(Date.now());
    } catch (e: any) {
      setChainErr(e?.response?.data?.error?.message || 'Option chain unavailable');
    } finally { setLoading(false); }
  }, [base]);
  useEffect(() => { if (open) fetchChain(); }, [open, fetchChain]);

  const minH = minHours.trim() === '' ? null : Number(minHours);
  const resolved = useMemo(() => (chain
    ? (expIso ? resolveFixedExpiry(chain.expiries, expIso, minH, nowMs) : resolveExpiry(chain.expiries, { tier, minHours: minH }, nowMs))
    : null), [chain, tier, minH, nowMs, expIso]);
  // The response ships full rows for one expiry; fetch the resolved one when it differs.
  useEffect(() => {
    if (open && resolved && chain?.chain && chain.chain.iso !== resolved.iso && !loading) fetchChain(resolved.iso);
  }, [open, resolved, chain, loading, fetchChain]);

  const tpl = findTemplate(templateId) || list[0].tpl;
  const at = levelValue != null && levelValue > 0 ? levelValue : (chain?.spot || 0);
  const rowsNow = chain?.chain && resolved && chain.chain.iso === resolved.iso ? chain.chain.rows : null;
  const settleMs = resolved?.settleMs || 0;
  const rowsAt = useMemo(() => (rowsNow && at > 0 ? chainAtSpot(rowsNow, at, settleMs, nowMs) : null), [rowsNow, at, settleMs, nowMs]);
  const tplBuilt = useMemo(() => (rowsAt ? buildTemplate(tpl, rowsAt, at, settleMs, nowMs) : null), [rowsAt, tpl, at, settleMs, nowMs]);
  const built = useMemo(() => (editLegs ? (rowsAt ? buildCustomLegs(editLegs, strikeMode, rowsAt, at) : null) : tplBuilt), [editLegs, strikeMode, rowsAt, at, tplBuilt]);
  const atmAt = useMemo(() => (rowsAt ? tplAtm(rowsAt, at) : null), [rowsAt, at]);
  // Strikes the editor offers: the resolved expiry's listed strikes priced on that side.
  const sideStrikes = useCallback((t: 'CE' | 'PE') => (rowsAt || [])
    .filter(r => { const q = t === 'CE' ? r.ce : r.pe; return !!q && ((q.mark ?? 0) > 0 || (q.bid ?? 0) > 0 || (q.ask ?? 0) > 0); })
    .map(r => r.strike).sort((a, b) => a - b), [rowsAt]);
  const startEdit = () => {
    if (!tplBuilt || atmAt == null || !rowsAt) return;
    setEditLegs(tplBuilt.map(l => ({ action: l.action, t: l.optionType, strike: l.strike, offset: legOffset(rowsAt, atmAt, l.strike), lots: l.lots })));
  };
  const patchLeg = (i: number, p: Partial<TplCustomLeg>) => setEditLegs(prev => {
    if (!prev) return prev;
    const n = prev.slice();
    const l = { ...n[i], ...p };
    if (p.strike != null && rowsAt && atmAt != null) l.offset = legOffset(rowsAt, atmAt, p.strike);
    n[i] = l; return n;
  });
  const stepLeg = (i: number, d: number) => {
    if (!editLegs) return;
    const l = editLegs[i];
    const ks = sideStrikes(l.t);
    const cur = strikeOf(l);
    const j = ks.indexOf(cur);
    const k = ks[Math.max(0, Math.min(ks.length - 1, (j < 0 ? ks.findIndex(x => x >= cur) : j) + d))];
    if (k != null) patchLeg(i, { strike: k });
  };
  // The strike a leg resolves to in the preview (OFFSET legs follow the ATM at the level).
  const strikeOf = (l: TplCustomLeg): number => {
    if (strikeMode === 'OFFSET' && rowsAt && atmAt != null) {
      const ks = rowsAt.map(r => r.strike).sort((a, b) => a - b);
      const ia = ks.indexOf(atmAt);
      if (ia >= 0) return ks[Math.max(0, Math.min(ks.length - 1, ia + l.offset))];
    }
    return l.strike;
  };
  const addLeg = (action: 'BUY' | 'SELL', t: 'CE' | 'PE') => {
    if (atmAt == null) return;
    // Start at the ATM; if a leg of this type already sits there, step out-of-the-money to the
    // first free listed strike (a Buy and a Sell at one strike would just cancel out).
    const ks = sideStrikes(t);
    const used = new Set((editLegs || []).filter(l => l.t === t).map(strikeOf));
    let j = ks.indexOf(atmAt);
    let k = atmAt;
    if (j >= 0) {
      const dir = t === 'CE' ? 1 : -1;
      while (j >= 0 && j < ks.length && used.has(ks[j])) j += dir;
      if (j >= 0 && j < ks.length) k = ks[j];
    }
    const off = rowsAt ? legOffset(rowsAt, atmAt, k) : 0;
    setEditLegs(prev => (prev && prev.length >= MAX_CUSTOM_LEGS ? prev : [...(prev || []), { action, t, strike: k, offset: off, lots: 1 }]));
  };
  const shiftAll = (d: number) => {
    if (!editLegs || !rowsAt || atmAt == null) return;
    setEditLegs(editLegs.map(l => {
      const ks = sideStrikes(l.t);
      const j = ks.indexOf(strikeOf(l));
      const k = j < 0 ? strikeOf(l) : ks[Math.max(0, Math.min(ks.length - 1, j + d))];
      return { ...l, strike: k, offset: legOffset(rowsAt, atmAt, k) };
    }));
  };
  const addWings = () => {
    if (!editLegs || !rowsAt || atmAt == null) return;
    const add: TplCustomLeg[] = [];
    for (const s0 of editLegs.filter(l => l.action === 'SELL')) {
      const k0 = strikeOf(s0);
      const covered = editLegs.some(b => b.action === 'BUY' && b.t === s0.t && (s0.t === 'CE' ? strikeOf(b) > k0 : strikeOf(b) < k0));
      if (covered) continue;
      const ks = sideStrikes(s0.t);
      const j = ks.indexOf(k0);
      const k = ks[Math.max(0, Math.min(ks.length - 1, j + (s0.t === 'CE' ? 2 : -2)))];
      if (k != null && k !== k0) add.push({ action: 'BUY', t: s0.t, strike: k, offset: legOffset(rowsAt, atmAt, k), lots: s0.lots });
    }
    if (!add.length) { toast('Every short already has a wing', { icon: '✓' }); return; }
    setEditLegs([...editLegs, ...add].slice(0, MAX_CUSTOM_LEGS));
  };
  const cv = chain?.contractValue || (base === 'BTC' ? 0.001 : 0.01);
  const atmIv = useMemo(() => {
    if (!rowsNow || !chain) return 0.5;
    const k = tplAtm(rowsNow, chain.spot);
    const r = rowsNow.find(x => x.strike === k);
    const ivs = [r?.ce?.iv, r?.pe?.iv].filter((x): x is number => x != null && x > 0);
    return ivs.length ? ivs.reduce((a, b) => a + b, 0) / ivs.length : 0.5;
  }, [rowsNow, chain]);

  // Risk cap → the lots the fire would actually use.
  const wsLegs = useCallback((n: number): WsLeg[] => (built || []).map((l, i) => ({
    id: `p${i}`, action: l.action, optionType: l.optionType, strike: l.strike, expiry: resolved!.iso, settleMs,
    lots: l.lots * n, price: l.price, priceMode: 'MKT', enabled: true, iv: l.iv || atmIv, symbol: l.symbol,
  })), [built, resolved, settleMs, atmIv]);
  const one = useMemo(() => (built && resolved ? computeStats(wsLegs(1), at, cv, atmIv, nowMs) : null), [built, resolved, wsLegs, at, cv, atmIv, nowMs]);
  const perLotLossInr = one && Number.isFinite(one.maxLoss) ? Math.abs(one.maxLoss) * USD_TO_INR : null;
  const capV = numOrNull(maxRisk);
  const effLots = capV != null && perLotLossInr != null && perLotLossInr > 0 ? Math.min(lots, Math.floor(capV / perLotLossInr)) : lots;
  const capRefuses = capV != null && perLotLossInr != null && perLotLossInr > capV;
  const legsN = useMemo(() => (built && resolved ? wsLegs(Math.max(1, effLots)) : []), [built, resolved, wsLegs, effLots]);
  const stats = useMemo(() => (legsN.length ? computeStats(legsN, at, cv, atmIv, nowMs) : null), [legsN, at, cv, atmIv, nowMs]);
  const credit = stats ? stats.net * USD_TO_INR : 0;          // + received, − paid
  const maxP = stats && Number.isFinite(stats.maxProfit) ? stats.maxProfit * USD_TO_INR : null;
  const maxL = stats && Number.isFinite(stats.maxLoss) ? Math.abs(stats.maxLoss) * USD_TO_INR : null;

  // An exit the position can never reach (sized for this many lots at the level).
  const tpN0 = numOrNull(tp), slN0 = numOrNull(sl);
  const tpUnreachable = tpN0 != null && maxP != null && maxP > 0 && tpN0 > maxP * 1.001;
  const slUnreachable = slN0 != null && maxL != null && maxL > 0 && slN0 > maxL * 1.001;

  // Sensible exit defaults until the user edits them: TP = half the max profit; SL = the
  // credit (credit trades) or half the debit (debit trades).
  useEffect(() => {
    if (touchedExits || !stats) return;
    const tpD = maxP != null ? maxP * 0.5 : Math.abs(credit);
    const slD = credit > 0 ? credit : Math.abs(credit) * 0.5;
    setTp(tpD >= 1 ? String(Math.round(tpD)) : '');
    setSl(slD >= 1 ? String(Math.round(slD)) : '');
  }, [stats, maxP, credit, touchedExits]);

  // Today's quotes on the strikes it would pick — the fire re-checks these.
  const liveIssues = useMemo(() => {
    if (!built || !rowsNow) return [];
    const out: string[] = [];
    for (const l of built) {
      const r = rowsNow.find(x => x.strike === l.strike);
      const q = r ? (l.optionType === 'CE' ? r.ce : r.pe) : null;
      const why = legLiquidityProblem({ action: l.action, symbol: l.symbol, bid: q?.bid ?? null, ask: q?.ask ?? null }, Number(spread) || 15);
      if (why) out.push(why);
    }
    return out;
  }, [built, rowsNow, spread]);

  // ── validation (the server re-checks every rule) ──
  const tgtN = numOrNull(tgtU), slUN = numOrNull(slU), tpN = numOrNull(tp), slN = numOrNull(sl);
  const errors: string[] = [];
  if (levelValue == null) errors.push(`Set the ${LEVEL_SHORT[levelKey]} level on the chart first.`);
  if (slN == null && slUN == null) errors.push('Set a stop: a ₹ stop-loss, a price stop, or both.');
  if (levelValue != null && tgtN != null && (long ? tgtN <= levelValue : tgtN >= levelValue)) errors.push(`The price target must be ${long ? 'above' : 'below'} ${fmtStrike(levelValue)}.`);
  if (levelValue != null && slUN != null && (long ? slUN >= levelValue : slUN <= levelValue)) errors.push(`The price stop must be ${long ? 'below' : 'above'} ${fmtStrike(levelValue)}.`);
  if (capRefuses) errors.push(`One lot risks ${inr(perLotLossInr!)} — above your ${inr(capV!)} cap, so it would not open.`);
  if (editLegs && rowsNow && !built) errors.push(strikeMode === 'FIXED' ? 'One of your strikes is not listed and priced on this expiry — pick another strike or expiry.' : 'Your legs cannot be priced on this expiry right now.');
  if (editLegs && editLegs.length === 0) errors.push('Add at least one leg.');
  if (isLive && noDeltaKey) errors.push('LIVE needs an active Delta API key (Settings → Broker) — or switch to PAPER.');

  const save = async () => {
    setSaving(true);
    try {
      await levelOptionTriggersService.arm({
        instrumentId, levelKey, templateId: tpl.id,
        expiryTier: expIso && chain ? tierOfExpiry(chain.expiries, expIso, Date.now()) : tier,
        expiryIso: expIso, customLegs: editLegs, strikeMode: editLegs ? strikeMode : null, minHoursToExpiry: minH,
        lots, maxRiskInr: capV, tpAmountInr: tpN, slAmountInr: slN, tgtUnderlying: tgtN, slUnderlying: slUN,
        autoTrail: false, trailArmPct: null, trailLockPct: null,   // no automatic trailing (2026-11-03)
        preExpiryExitMins: preExp.trim() === '' ? null : Math.floor(Number(preExp)),
        maxSpreadPct: numOrNull(spread) ?? 15, maxChasePct: numOrNull(chase) ?? 0.5, tradingMode: mode,
        // The red "Attach — LIVE" click is the confirmation the server asks for.
        ...(isLive ? { confirmLive: 'LIVE' } : {}),
      });
      toast.success(`${tpl.name} attached to ${label} (${mode})`);
      onSaved(); onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.message || 'Could not arm');
    } finally { setSaving(false); }
  };
  const disarm = async () => {
    if (!existing) return;
    setSaving(true);
    try { await levelOptionTriggersService.cancel(existing.id); toast.success(`Option strategy removed from ${label}`); onSaved(); onClose(); }
    catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Could not disarm'); }
    finally { setSaving(false); }
  };

  // ── small UI helpers ──
  const sect = (t: string, sub?: string) => (
    <Box sx={{ mt: 1.6, mb: 0.6 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: c.dim }}>{t}</Typography>
      {sub && <Typography sx={{ fontSize: 11.5, color: c.faint }}>{sub}</Typography>}
    </Box>
  );
  const field = (lbl: string, v: string, set: (s: string) => void, opts: { adorn?: string; w?: number; help?: string; exit?: boolean; placeholder?: string; warn?: boolean } = {}) => (
    <TextField size="small" label={lbl} value={v} type="number" placeholder={opts.placeholder}
      InputLabelProps={{ shrink: true }}
      onChange={e => { set(e.target.value); if (opts.exit) setTouchedExits(true); }}
      helperText={opts.help} FormHelperTextProps={opts.warn ? { sx: { color: c.amber, fontWeight: 600 } } : undefined}
      sx={{ width: opts.w || 150 }}
      InputProps={opts.adorn ? { startAdornment: <Typography sx={{ mr: 0.5, color: c.dim, fontSize: 13 }}>{opts.adorn}</Typography> } : undefined} />
  );

  const tplCard = (t: OptionTemplate, directional: boolean) => {
    const sel = t.id === tpl.id;
    const col = t.outlook === 'Bullish' ? c.green : t.outlook === 'Bearish' ? c.red : c.blue;
    return (
      <Box key={t.id} component="button" type="button" onClick={() => { setTemplateId(t.id); setEditLegs(null); }} data-tpl={t.id}
        sx={{
          all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1, p: 0.8, borderRadius: 1,
          border: `1px solid ${sel ? col : c.border}`, bgcolor: sel ? (c.isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)') : 'transparent',
          opacity: directional ? 1 : 0.85, '&:hover': { bgcolor: c.hover }, minWidth: 0,
        }}>
        <Glyph pts={t.glyph} color={col} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: sel ? 700 : 600, color: c.text, lineHeight: 1.2 }}>{t.name}</Typography>
          <Typography sx={{ fontSize: 11, color: c.dim, lineHeight: 1.2 }}>{t.blurb}</Typography>
        </Box>
      </Box>
    );
  };

  const own = list.filter(x => x.directional), other = list.filter(x => !x.directional);
  const preDefault = DEFAULT_PRE_EXPIRY[tier];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={narrow} data-testid="level-option-dialog">
      <DialogTitle sx={{ pr: 6, pb: 1 }}>
        <Typography component="span" sx={{ fontSize: 17, fontWeight: 700 }}>⚡ Option strategy on {label}</Typography>
        <Typography component="span" sx={{ fontSize: 13, color: c.dim, ml: 1 }}>{symbol} · opens when the level fires · </Typography>
        <Typography component="span" sx={{ fontSize: 13, fontWeight: 800, color: isLive ? c.red : c.dim }}>{isLive ? 'LIVE — real orders' : 'PAPER'}</Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="close"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1.5, md: 2 } }}>
        {!autoExecute && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            <b>Auto-Execute is OFF</b> — when {LEVEL_SHORT[levelKey]} fires you will get an alert, but this strategy will <b>not</b> open.
            Turn on <i>Auto-Execute</i> on the Dashboard's Pending Manual Trigger Watch card to let it open ({mode}).
          </Alert>
        )}
        {existing?.lastError && existing.status === 'ARMED' && existing.failCount > 0 && (
          <Alert severity="info" sx={{ mb: 1.5 }}>Last attempt: {existing.lastError}</Alert>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'minmax(0, 1.05fr) minmax(0, 1fr)', gap: 2.5 }}>
          {/* ── LEFT: configuration ── */}
          <Box sx={{ minWidth: 0 }}>
            {sect('Strategy', long ? 'Bullish first — this level opens a long' : 'Bearish first — this level opens a short')}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 0.8 }}>
              {own.map(x => tplCard(x.tpl, true))}
            </Box>
            <Typography sx={{ fontSize: 11.5, color: c.faint, mt: 1, mb: 0.5 }}>Other — not directional (they do not bet on the move this level signals)</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 0.8 }}>
              {other.map(x => tplCard(x.tpl, false))}
            </Box>

            {sect('Expiry', expIso ? 'This exact date: never rolled; not opened if it has settled or is too close' : 'A rule resolved when the level fires (from the expiries listed then), or pick a date')}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.8, '& > .MuiTextField-root': { ml: 0.8 } }}>
              {(['D0', 'D1', 'W0', 'M0'] as ExpiryTier[]).map(t => (
                <Chip key={t} label={TIER_LABEL[t]} onClick={() => { setTier(t); setExpIso(null); }} color={!expIso && tier === t ? 'primary' : 'default'}
                  variant={!expIso && tier === t ? 'filled' : 'outlined'} size="small" />
              ))}
              {field('Skip if under (hours)', minHours, setMinHours, { w: 150, placeholder: expIso ? '0.5 (default)' : `${DEFAULT_MIN_HOURS[tier]} (default)` })}
            </Box>
            <Box data-testid="level-option-dates" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.8, pb: 0.4 }}>
              {(chain?.expiries || []).filter(e => e.settleMs > nowMs).map(e => {
                const on = expIso === e.iso;
                return (
                  <Box key={e.iso} component="button" type="button" data-exp={e.iso}
                    onClick={() => { setExpIso(on ? null : e.iso); if (!on && !editLegs) setStrikeMode('FIXED'); }}
                    sx={{ all: 'unset', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 700, px: 0.8, py: 0.3, borderRadius: 1,
                      border: `1px solid ${on ? c.amber : c.border}`, bgcolor: on ? c.amberBg : 'transparent', color: on ? c.text : c.dim, '&:hover': { bgcolor: c.hover } }}>
                    {expLabel(e.iso)} <Box component="span" sx={{ color: on ? c.amber : c.faint, fontWeight: 600, fontSize: 10.5 }}>{timeLeft(e.settleMs, nowMs)}</Box>
                    {isMonthlyExpiry(e.iso) && <Box component="span" sx={{ ml: 0.4, px: 0.35, borderRadius: 0.5, fontSize: 9, fontWeight: 800, color: c.blue, border: `1px solid ${c.blue}` }}>M</Box>}
                  </Box>
                );
              })}
            </Box>

            {sect('Size')}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1.2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button size="small" variant="outlined" sx={{ minWidth: 30, px: 0 }} onClick={() => setLots(l => Math.max(1, l - 1))}>−</Button>
                <TextField size="small" label="Lots" value={lots} type="number" sx={{ width: 80 }}
                  onChange={e => setLots(Math.max(1, Math.min(1000, Math.floor(Number(e.target.value) || 1))))} />
                <Button size="small" variant="outlined" sx={{ minWidth: 30, px: 0 }} onClick={() => setLots(l => Math.min(1000, l + 1))}>+</Button>
              </Box>
              {field('Max ₹ risk (optional)', maxRisk, setMaxRisk, { adorn: '₹', w: 170, help: capV != null && !capRefuses && effLots < lots ? `→ ${effLots} lot(s) fit the cap` : 'lots are cut to fit' })}
            </Box>

            {sect('Exits', 'Whichever is reached first closes the whole position')}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2 }}>
              {field('Take profit ₹', tp, setTp, { adorn: '₹', exit: true, w: 175, ...(tpUnreachable ? { warn: true, help: `above max profit ${inr(maxP!)} — can't be hit` } : { help: 'net, on total P&L' }) })}
              {field('Stop loss ₹', sl, setSl, { adorn: '₹', exit: true, w: 175, ...(slUnreachable ? { warn: true, help: `beyond max loss ${inr(maxL!)} — never hit` } : { help: 'net, on total P&L' }) })}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2, mt: 1 }}>
              {field(`${base} target price`, tgtU, setTgtU, { exit: true, help: long ? 'above the level' : 'below the level' })}
              {field(`${base} stop price`, slU, setSlU, { exit: true, help: long ? 'below the level' : 'above the level' })}
            </Box>
            <Typography sx={{ fontSize: 11.5, color: c.dim, mt: 0.8 }}>
              Both ₹ amounts are <b>net</b> — what you receive after closing at the bid / ask and the entry and exit fees.
              The stop does not trail by itself: once it fires, drag the stop on the basket chart to lock in profit.
            </Typography>

            <Box component="button" type="button" onClick={() => setAdv(a => !a)}
              sx={{ all: 'unset', cursor: 'pointer', mt: 1.4, fontSize: 12, color: c.blue }}>{adv ? '▾' : '▸'} Advanced</Box>
            <Collapse in={adv}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2, mt: 1 }}>
                {field('Close before expiry (min)', preExp, setPreExp, { w: 180, help: `default ${preDefault} min` })}
                {field('Max spread % of mid', spread, setSpread, { w: 150, help: 'per leg; wider → not opened' })}
                {field('Retry while within %', chase, setChase, { w: 150, help: 'of the level, for ≤ 10 min' })}
              </Box>
            </Collapse>

            {sect('Mode')}
            <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}>
              <Chip label="LIVE — real orders" size="small" data-mode="LIVE" onClick={() => setMode('LIVE')} disabled={noDeltaKey}
                color={isLive ? 'error' : 'default'} variant={isLive ? 'filled' : 'outlined'} />
              <Chip label="PAPER — simulated" size="small" data-mode="PAPER" onClick={() => setMode('PAPER')}
                color={!isLive ? 'primary' : 'default'} variant={!isLive ? 'filled' : 'outlined'} />
            </Box>
            {noDeltaKey && (
              <Typography sx={{ fontSize: 11.5, color: c.dim, mt: 0.6 }}>LIVE needs an active Delta API key (Settings → Broker).</Typography>
            )}
            <Collapse in={isLive}>
              <Box data-testid="level-option-live-note" sx={{ mt: 1, px: 1.2, py: 0.8, borderRadius: 1, borderLeft: `3px solid ${c.red}`, bgcolor: 'action.hover', fontSize: 12.5, lineHeight: 1.5 }}>
                Places <b>real market orders on Delta</b> when {LEVEL_SHORT[levelKey]} fires{autoExecute ? '' : ' (with Auto-Execute ON)'}.
                {maxL != null ? <> Max loss ≈ <b>{inr(maxL)}</b>.</> : null}
                {funds && funds.status.delta === 'ok' ? <> Delta available <b>{inr(funds.byBroker.delta)}</b>.</> : null}
                {' '}<Tooltip arrow title="At the moment the level fires the server reads your Delta wallet again. If it can't be read, or it doesn't cover the margin + 10%, nothing is placed and you get a Telegram. Protective BUY legs are placed before SELL legs; the kill switch and daily-loss cap apply.">
                  <span style={{ textDecoration: 'underline dotted', cursor: 'help' }}>Safety checks</span>
                </Tooltip>
              </Box>
            </Collapse>
          </Box>

          {/* ── RIGHT: preview ── */}
          <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>If it fired at {fmtStrike(Math.round(at))} now</Typography>
              {loading && <CircularProgress size={14} />}
              <Box sx={{ flex: 1 }} />
              <Tooltip title="Refresh prices"><IconButton size="small" onClick={() => fetchChain(resolved?.iso)}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
            </Box>
            {chainErr && <Alert severity="error">{chainErr}</Alert>}
            {!chain && !chainErr && (
              <Typography sx={{ fontSize: 12.5, color: c.dim }}>Loading today's {base} option chain from Delta…</Typography>
            )}
            {chain && !resolved && <Alert severity="warning">No {TIER_LABEL[tier].toLowerCase()} expiry far enough out is listed — it would not open.</Alert>}
            {resolved && (
              <Typography sx={{ fontSize: 12.5, color: c.dim }}>
                Expiry <b style={{ color: c.text as any }}>{expLabel(resolved.iso)}</b> ({timeLeft(resolved.settleMs, nowMs)} left)
                {resolved.rolled ? ' — the nearest one is too close, so it rolls to this one' : ''} · spot now {chain ? fmtStrike(Math.round(chain.spot)) : '—'}
              </Typography>
            )}
            {rowsNow && !built && <Alert severity="warning">{tpl.name} can't be built on this expiry right now (strikes unpriced).</Alert>}
            {built && (
              <>
                <Typography sx={{ fontSize: 13 }}>{editLegs ? `Your legs (started from ${tpl.name})` : describe(tpl, built, base)}</Typography>
              </>
            )}
            {(built || editLegs) && rowsNow && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {!editLegs ? (
                    <Button size="small" variant="outlined" onClick={startEdit} disabled={!tplBuilt} data-testid="level-option-edit-legs">Edit strikes / legs</Button>
                  ) : (
                    <>
                      <Chip size="small" label="Exact strikes" data-smode="FIXED" onClick={() => setStrikeMode('FIXED')}
                        color={strikeMode === 'FIXED' ? 'primary' : 'default'} variant={strikeMode === 'FIXED' ? 'filled' : 'outlined'} />
                      <Chip size="small" label="Same distance from ATM" data-smode="OFFSET" onClick={() => setStrikeMode('OFFSET')}
                        color={strikeMode === 'OFFSET' ? 'primary' : 'default'} variant={strikeMode === 'OFFSET' ? 'filled' : 'outlined'} />
                      <Box sx={{ flex: 1 }} />
                      <Button size="small" onClick={() => setEditLegs(null)}>Reset to {tpl.name}</Button>
                    </>
                  )}
                </Box>
                {editLegs && (
                  <Typography sx={{ fontSize: 11.5, color: c.dim }}>
                    {strikeMode === 'FIXED'
                      ? 'These exact strikes are traded when the level fires. If one is no longer listed, the strategy is not opened.'
                      : `Each leg keeps its distance from the ATM at the fire, in listed strikes${atmAt != null ? ` (ATM at the level is ${fmtStrike(atmAt)})` : ''}, so the shape follows price.`}
                  </Typography>
                )}
                <Box component="table" data-testid="level-option-legs" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, '& td, & th': { py: 0.4, px: 0.6, borderBottom: `1px solid ${c.border}`, textAlign: 'right' }, '& th': { color: c.dim, fontWeight: 600 }, '& td:first-of-type, & th:first-of-type': { textAlign: 'left' } }}>
                  <thead><tr><th>Leg</th><th style={{ textAlign: 'center' }}>Strike</th><th>Lots</th><th>Price at level</th>{editLegs && <th />}</tr></thead>
                  <tbody>
                    {editLegs ? editLegs.map((l, i) => {
                      const b = built ? built[i] : null;
                      const k = strikeOf(l);
                      const ks = sideStrikes(l.t);
                      const step = (lbl: string, d: number) => (
                        <Box component="button" type="button" onClick={() => stepLeg(i, d)} sx={{ all: 'unset', cursor: 'pointer', px: 0.6, fontWeight: 800, color: c.dim, '&:hover': { color: c.text } }}>{lbl}</Box>
                      );
                      return (
                        <tr key={i} data-leg={i}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <Box component="button" type="button" onClick={() => patchLeg(i, { action: l.action === 'BUY' ? 'SELL' : 'BUY' })}
                              sx={{ all: 'unset', cursor: 'pointer', fontWeight: 800, fontSize: 11, px: 0.6, borderRadius: 0.6, mr: 0.5, bgcolor: l.action === 'BUY' ? c.greenBg : c.redBg, color: l.action === 'BUY' ? c.green : c.red }}>{l.action}</Box>
                            <Box component="button" type="button" onClick={() => patchLeg(i, { t: l.t === 'CE' ? 'PE' : 'CE' })}
                              sx={{ all: 'unset', cursor: 'pointer', fontWeight: 700, fontSize: 11.5, color: l.t === 'CE' ? c.call : c.put }}>{l.t === 'CE' ? 'Call' : 'Put'}</Box>
                          </td>
                          <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                            {step('−', -1)}
                            <Box component="select" value={k} onChange={(e: any) => patchLeg(i, { strike: Number(e.target.value) })}
                              sx={{ all: 'unset', cursor: 'pointer', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: b ? c.text : c.red, '& option': { color: '#000' } }}>
                              {(ks.indexOf(k) >= 0 ? ks : [...ks, k].sort((x, y) => x - y)).map(x => <option key={x} value={x}>{fmtStrike(x)}</option>)}
                            </Box>
                            {step('+', 1)}
                            {strikeMode === 'OFFSET' && <Box component="span" sx={{ fontSize: 10, color: c.faint, ml: 0.4 }}>{l.offset === 0 ? 'ATM' : `ATM${l.offset > 0 ? '+' : ''}${l.offset}`}</Box>}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <Box component="button" type="button" onClick={() => patchLeg(i, { lots: Math.max(1, l.lots - 1) })} sx={{ all: 'unset', cursor: 'pointer', px: 0.5, color: c.dim }}>{'−'}</Box>
                            {l.lots * Math.max(1, effLots)}
                            <Box component="button" type="button" onClick={() => patchLeg(i, { lots: Math.min(20, l.lots + 1) })} sx={{ all: 'unset', cursor: 'pointer', px: 0.5, color: c.dim }}>+</Box>
                          </td>
                          <td>{b ? `$${b.price.toFixed(1)}` : <span style={{ color: c.red }}>not priced</span>}</td>
                          <td><Box component="button" type="button" aria-label="remove leg" onClick={() => setEditLegs(editLegs.filter((_, j) => j !== i))} sx={{ all: 'unset', cursor: 'pointer', color: c.faint, '&:hover': { color: c.red } }}>{'✕'}</Box></td>
                        </tr>
                      );
                    }) : (built || []).map((l, i) => (
                      <tr key={i}>
                        <td style={{ color: l.action === 'SELL' ? c.red : c.green, fontWeight: 600 }}>{l.action} {l.optionType === 'CE' ? 'Call' : 'Put'}</td>
                        <td style={{ textAlign: 'center' }}>{fmtStrike(l.strike)}</td>
                        <td>{l.lots * Math.max(1, effLots)}</td>
                        <td>${l.price.toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Box>
                {editLegs && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap', fontSize: 12 }}>
                    {([['BUY', 'CE'], ['SELL', 'CE'], ['BUY', 'PE'], ['SELL', 'PE']] as Array<['BUY' | 'SELL', 'CE' | 'PE']>).map(([a, t]) => (
                      <Button key={a + t} size="small" variant="text" disabled={editLegs.length >= MAX_CUSTOM_LEGS} onClick={() => addLeg(a, t)}
                        sx={{ minWidth: 0, px: 0.8, fontSize: 11.5, color: a === 'BUY' ? c.green : c.red }}>+ {a === 'BUY' ? 'Buy' : 'Sell'} {t === 'CE' ? 'Call' : 'Put'}</Button>
                    ))}
                    <Box sx={{ flex: 1 }} />
                    {editLegs.some(l => l.action === 'SELL') && <Button size="small" onClick={addWings} sx={{ fontSize: 11.5 }}>+ Add wings</Button>}
                    <Tooltip title="Move every leg one listed strike"><span>
                      <Button size="small" onClick={() => shiftAll(-1)} sx={{ minWidth: 0, px: 0.8 }}>{'◀'} Shift</Button>
                      <Button size="small" onClick={() => shiftAll(1)} sx={{ minWidth: 0, px: 0.8 }}>Shift {'▶'}</Button>
                    </span></Tooltip>
                  </Box>
                )}
              </>
            )}
            {built && (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 0.8 }}>
                  {[
                    [credit >= 0 ? 'Credit' : 'Debit', inr(Math.abs(credit)), credit >= 0 ? c.green : c.text],
                    ['Max profit', maxP != null ? inr(maxP) : 'unlimited', c.green],
                    ['Max loss', maxL != null ? inr(maxL) : 'unlimited', c.red],
                    ['Break-even', stats && stats.breakevens.length ? stats.breakevens.map(b => fmtStrike(Math.round(b))).join(' / ') : '—', c.text],
                  ].map(([k, v, col]) => (
                    <Box key={k as string} sx={{ border: `1px solid ${c.border}`, borderRadius: 1, p: 0.8 }}>
                      <Typography sx={{ fontSize: 11, color: c.dim }}>{k}</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: col as any }}>{v}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ height: 230 }}>
                  <PayoffGraph legs={legsN} spot={chain?.spot || at} whatIfSpot={at} atMs={nowMs} horizon={settleMs} ivShift={0}
                    cv={cv} atmIv={atmIv} breakevens={stats?.breakevens || []} ccy="INR" showProb={false} c={c} />
                </Box>
                {liveIssues.length > 0 && (
                  <Alert severity="info" sx={{ py: 0 }}>
                    Right now: {liveIssues.join('; ')}. When the level fires the quotes are checked again — if a leg still can't trade, it retries for up to 10 minutes.
                  </Alert>
                )}
              </>
            )}
            <Typography sx={{ fontSize: 11.5, color: c.faint }}>
              The preview re-prices today's chain at the level, each contract keeping its current IV. Strikes, expiry and prices are
              worked out again at the moment {LEVEL_SHORT[levelKey]} fires, so the opened position can differ — Telegram reports exactly what opened.
            </Typography>
          </Box>
        </Box>
        {errors.length > 0 && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>{errors.map((e, i) => <div key={i}>{e}</div>)}</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.2 }}>
        {existing && <Button color="error" onClick={disarm} disabled={saving}>Remove strategy</Button>}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" color={isLive ? 'error' : 'primary'} onClick={save} disabled={saving || errors.length > 0} data-testid="level-option-arm">
          {isLive ? (existing ? 'Update — LIVE' : 'Attach — LIVE (real orders)') : (existing ? 'Update — PAPER' : 'Attach — PAPER')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LevelOptionDialog;
